import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { useDebouncedCallback } from 'use-debounce'
import {
  Button,
  ButtonBlock,
  FieldBlock,
  MultiSelectField,
  PageHeader,
  SelectField,
  TextArea,
  useToast,
} from '@politechdev/blocks-design-system'
import {
  CanvasserSelectField,
  UserSelectField,
  View,
  ViewContainer,
} from 'components'
import { SelectableCanvasser } from 'components/CanvasserSelectField/types'
import { SelectableUser } from 'components/UserSelectField/types'
import { useRequest } from 'hooks'
import { useCurrent } from 'contexts'
import { createPerformanceReview } from 'requests/performanceReviews'
import {
  fetchCanvasserFlags,
  fetchCanvasserReviewReasons,
  formatForCreate,
  getFlagOptions,
  getReviewReasonOptions,
  hasMissingFields,
} from './utils'
import { PerformanceReviewFormData } from './types'
import { PULL_OPTIONS } from './constants'

const CreatePerformanceReviews = () => {
  const { t } = useTranslation()
  const { setToast } = useToast()
  const history = useHistory()
  const { currentUser } = useCurrent()

  const [performanceReview, setPerformanceReview] =
    useState<PerformanceReviewFormData>({
      flag_ids: [],
      reasons: [],
      requested_by_user: currentUser,
    })

  const updateField = (update: Partial<PerformanceReviewFormData>) => {
    setPerformanceReview(current => ({ ...current, ...update }))
  }

  const fetchCanvasserFlagsRequest = useRequest(fetchCanvasserFlags, {
    onError: () => {
      setToast({
        variant: 'error',
        message: t('An issue occured fetching canvasser flags'),
      })
    },
  })

  const fetchReviewReasonsRequest = useRequest(fetchCanvasserReviewReasons, {
    onError: () => {
      setToast({
        variant: 'error',
        message: t('An issue occured fetching reasons for review'),
      })
    },
  })

  useEffect(() => {
    const canvasserId = performanceReview.canvasser?.id
    if (canvasserId) {
      void fetchCanvasserFlagsRequest.makeRequest(canvasserId)
      void fetchReviewReasonsRequest.makeRequest(canvasserId)
    }
  }, [performanceReview.canvasser?.id])

  const navigateToList = () => {
    history.push('/performance_reviews')
  }

  const createPerformanceReviewRequest = useRequest(createPerformanceReview, {
    onSuccess: () => {
      navigateToList()
    },
  })

  const handleCreatePerformanceReview = () => {
    const reviewAttributes = formatForCreate(performanceReview)
    void createPerformanceReviewRequest.makeRequest(reviewAttributes)
  }

  const [debouncedFetchFlags] = useDebouncedCallback(
    (canvasserId: number, query: string) =>
      fetchCanvasserFlagsRequest.makeRequest(canvasserId, query),
    250
  )

  const handleFlagsInputChange = (value: string | undefined) => {
    if (performanceReview.canvasser?.id && value) {
      void debouncedFetchFlags(performanceReview.canvasser.id, value)
    }
  }

  const flagOptions = useMemo(
    () =>
      fetchCanvasserFlagsRequest.response
        ? getFlagOptions(fetchCanvasserFlagsRequest.response)
        : [],
    [fetchCanvasserFlagsRequest.response]
  )

  const reasonOptions = fetchReviewReasonsRequest.response
    ? getReviewReasonOptions(fetchReviewReasonsRequest.response)
    : []

  return (
    <View>
      <PageHeader title={t('New performance reviews')} />
      <ViewContainer>
        <FieldBlock collapsed>
          <UserSelectField
            label={t('Requested by')}
            user={performanceReview.requested_by_user}
            onSelect={(user: SelectableUser | undefined) =>
              updateField({ requested_by_user: user })
            }
            required
            error={!!createPerformanceReviewRequest.errors.requested_by_user_id}
            errorMessage={
              createPerformanceReviewRequest.errors.requested_by_user_id
            }
          />
        </FieldBlock>
        <FieldBlock collapsed>
          <CanvasserSelectField
            label={t('Canvasser under review')}
            selectedCanvasser={performanceReview.canvasser}
            onSelect={(canvasser: SelectableCanvasser | undefined | null) =>
              updateField({ canvasser })
            }
            filters={undefined}
            required
            disableNonCollectingTurfs={false}
            error={!!createPerformanceReviewRequest.errors.canvasser_id}
            errorMessage={createPerformanceReviewRequest.errors.canvasser_id}
          />
        </FieldBlock>
        <FieldBlock collapsed>
          <UserSelectField
            label={t('Assigned for review')}
            user={performanceReview.assigned_to_user}
            onSelect={(user: SelectableUser | undefined) =>
              updateField({ assigned_to_user: user })
            }
            error={!!createPerformanceReviewRequest.errors.assigned_to_user_id}
            errorMessage={
              createPerformanceReviewRequest.errors.assigned_to_user_id
            }
          />
        </FieldBlock>
        <FieldBlock collapsed>
          <SelectField
            label={t('Pull canvasser from field')}
            options={PULL_OPTIONS}
            onSelect={value => {
              updateField({
                pull_from_field: value,
              })
            }}
            error={!!createPerformanceReviewRequest.errors.pull_from_field}
            errorMessage={createPerformanceReviewRequest.errors.pull_from_field}
            required
          />
        </FieldBlock>
        <FieldBlock collapsed variant="large">
          <MultiSelectField
            disabled={!performanceReview.canvasser}
            label={t('Associated flags')}
            loading={fetchCanvasserFlagsRequest.isLoading}
            options={flagOptions}
            onSelect={values =>
              updateField({
                flag_ids: values,
              })
            }
            onInputChange={handleFlagsInputChange}
            values={performanceReview.flag_ids}
            icon={undefined}
          />
        </FieldBlock>
        <FieldBlock collapsed variant="large">
          <MultiSelectField
            disabled={!performanceReview.canvasser}
            label={t('Reason for review')}
            loading={fetchReviewReasonsRequest.isLoading}
            values={performanceReview.reasons}
            options={reasonOptions}
            onSelect={values =>
              updateField({
                reasons: values,
              })
            }
            icon={undefined}
            required
            error={!!createPerformanceReviewRequest.errors.reasons}
            errorMessage={createPerformanceReviewRequest.errors.reasons}
          />
        </FieldBlock>
        <FieldBlock collapsed variant="large">
          <TextArea
            label={t('Additional details')}
            onChange={value =>
              updateField({
                additional_details: value as string,
              })
            }
            value={performanceReview.additional_details ?? ''}
            required
            error={!!createPerformanceReviewRequest.errors.additional_details}
            errorMessage={
              createPerformanceReviewRequest.errors.additional_details
            }
          />
        </FieldBlock>
        <ButtonBlock>
          <Button.Accent
            onClick={handleCreatePerformanceReview}
            disabled={
              createPerformanceReviewRequest.isLoading ||
              hasMissingFields(performanceReview)
            }
          >
            {t('Open review')}
          </Button.Accent>
          <Button.Secondary
            onClick={navigateToList}
            disabled={createPerformanceReviewRequest.isLoading}
          >
            {t('Cancel')}
          </Button.Secondary>
        </ButtonBlock>
      </ViewContainer>
    </View>
  )
}

export default CreatePerformanceReviews
